export interface ComplianceTask {
  id: string;
  title: string;
  description: string | null;
  priority: 'p0' | 'p1' | 'p2' | 'p3';
  status: 'not_started' | 'in_progress' | 'at_risk' | 'blocked' | 'complete';
  department_id: string | null;
  due_date: string | null;
  completed_at: string | null;
}

export interface Department {
  id: string;
  name: string;
  color: string;
}

export interface TaskMetrics {
  totalTasks: number;
  completedTasks: number;
  overdueTasks: number;
  inProgressTasks: number;
  atRiskTasks: number;
  blockedTasks: number;
}

export interface DepartmentScore {
  departmentId: string;
  departmentName: string;
  departmentColor: string;
  score: number;
  totalTasks: number;
  completedTasks: number;
  overdueTasks: number;
  inProgressTasks: number;
  atRiskTasks: number;
  blockedTasks: number;
}

export interface ComplianceScore {
  overallScore: number;
  departmentScores: DepartmentScore[];
  readinessStatus: 'ready' | 'watch' | 'action';
  metrics: TaskMetrics;
}

// Priority weights
const PRIORITY_WEIGHTS = {
  p0: 4,
  p1: 3,
  p2: 2,
  p3: 1,
};

// Status completion percentages
const STATUS_COMPLETION = {
  complete: 1.0,
  in_progress: 0.5,
  at_risk: 0.25,
  blocked: 0.0,
  not_started: 0.0,
};

export function calculateComplianceScore(
  tasks: ComplianceTask[],
  departments: Department[]
): ComplianceScore {
  const metrics: TaskMetrics = {
    totalTasks: tasks.length,
    completedTasks: 0,
    overdueTasks: 0,
    inProgressTasks: 0,
    atRiskTasks: 0,
    blockedTasks: 0,
  };

  const now = new Date();
  let totalWeight = 0;
  let completedWeight = 0;
  let overdueWeight = 0;

  // Calculate metrics and weights
  tasks.forEach((task) => {
    const weight = PRIORITY_WEIGHTS[task.priority];
    totalWeight += weight;

    // Count completed
    if (task.status === 'complete') {
      completedWeight += weight;
      metrics.completedTasks++;
    }

    // Count by status
    switch (task.status) {
      case 'in_progress':
        metrics.inProgressTasks++;
        break;
      case 'at_risk':
        metrics.atRiskTasks++;
        break;
      case 'blocked':
        metrics.blockedTasks++;
        break;
    }

    // Check if overdue
    if (task.due_date && task.status !== 'complete') {
      const dueDate = new Date(task.due_date);
      if (dueDate < now) {
        metrics.overdueTasks++;
        overdueWeight += weight;
      }
    }
  });

  // Calculate overall score
  let overallScore = totalWeight > 0 ? (completedWeight / totalWeight) * 100 : 100;

  // Apply overdue penalty
  if (overdueWeight > 0) {
    const overdueRatio = overdueWeight / totalWeight;
    const penalty = Math.min(overdueRatio * 25, 25); // Max 25% penalty
    overallScore = Math.max(overallScore - penalty, 0);
  }

  overallScore = Math.round(overallScore);

  // Calculate department-level scores
  const departmentScores: DepartmentScore[] = departments.map((dept) => {
    const deptTasks = tasks.filter((t) => t.department_id === dept.id);
    const deptTotalWeight = deptTasks.reduce(
      (sum, task) => sum + PRIORITY_WEIGHTS[task.priority],
      0
    );
    const deptCompletedWeight = deptTasks.reduce(
      (sum, task) =>
        sum + (task.status === 'complete' ? PRIORITY_WEIGHTS[task.priority] : 0),
      0
    );

    let deptScore = deptTotalWeight > 0 ? (deptCompletedWeight / deptTotalWeight) * 100 : 100;

    // Apply overdue penalty for department
    const deptOverdueWeight = deptTasks.reduce(
      (sum, task) => {
        if (task.due_date && task.status !== 'complete') {
          const dueDate = new Date(task.due_date);
          if (dueDate < now) {
            return sum + PRIORITY_WEIGHTS[task.priority];
          }
        }
        return sum;
      },
      0
    );

    if (deptOverdueWeight > 0) {
      const deptOverdueRatio = deptOverdueWeight / deptTotalWeight;
      const deptPenalty = Math.min(deptOverdueRatio * 25, 25);
      deptScore = Math.max(deptScore - deptPenalty, 0);
    }

    return {
      departmentId: dept.id,
      departmentName: dept.name,
      departmentColor: dept.color,
      score: Math.round(deptScore),
      totalTasks: deptTasks.length,
      completedTasks: deptTasks.filter((t) => t.status === 'complete').length,
      overdueTasks: deptTasks.filter((t) => {
        if (t.due_date && t.status !== 'complete') {
          return new Date(t.due_date) < now;
        }
        return false;
      }).length,
      inProgressTasks: deptTasks.filter((t) => t.status === 'in_progress').length,
      atRiskTasks: deptTasks.filter((t) => t.status === 'at_risk').length,
      blockedTasks: deptTasks.filter((t) => t.status === 'blocked').length,
    };
  });

  // Determine readiness status
  let readinessStatus: 'ready' | 'watch' | 'action';
  if (overallScore >= 80) {
    readinessStatus = 'ready';
  } else if (overallScore >= 65) {
    readinessStatus = 'watch';
  } else {
    readinessStatus = 'action';
  }

  return {
    overallScore,
    departmentScores,
    readinessStatus,
    metrics,
  };
}

// Helper to get priority color
export function getPriorityColor(priority: 'p0' | 'p1' | 'p2' | 'p3'): string {
  const colors = {
    p0: 'text-red-600 bg-red-50 dark:bg-red-950/30',
    p1: 'text-orange-600 bg-orange-50 dark:bg-orange-950/30',
    p2: 'text-yellow-600 bg-yellow-50 dark:bg-yellow-950/30',
    p3: 'text-gray-600 bg-gray-50 dark:bg-gray-950/30',
  };
  return colors[priority];
}

// Helper to get status color
export function getStatusColor(status: ComplianceTask['status']): string {
  const colors = {
    not_started: 'text-gray-600 bg-gray-50 dark:bg-gray-950/30',
    in_progress: 'text-blue-600 bg-blue-50 dark:bg-blue-950/30',
    at_risk: 'text-orange-600 bg-orange-50 dark:bg-orange-950/30',
    blocked: 'text-red-600 bg-red-50 dark:bg-red-950/30',
    complete: 'text-green-600 bg-green-50 dark:bg-green-950/30',
  };
  return colors[status];
}

// Helper to format status label
export function getStatusLabel(status: ComplianceTask['status']): string {
  const labels = {
    not_started: 'Not Started',
    in_progress: 'In Progress',
    at_risk: 'At Risk',
    blocked: 'Blocked',
    complete: 'Complete',
  };
  return labels[status];
}

